import { useState, useEffect, useCallback } from "react";
import {
  Box,
  VStack,
  Heading,
  HStack,
  Stack,
  Input,
  Button,
  Table,
  IconButton,
  Text,
  Spinner,
  Card,
  Dialog,
  Textarea,
} from "@chakra-ui/react";
import { FiEdit2, FiRefreshCw, FiPlus, FiTrash2 } from "react-icons/fi";
import api from "../lib/api";
import { toaster } from "../components/ui/toaster";

interface Location {
  id: number;
  state: string;
  lga?: string;
  wards?: string[];
  created_at: string;
}

export default function LocationsPage() {
  const [data, setData] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Edit/Create State
  const [isOpen, setIsOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentItem, setCurrentItem] = useState<{
    id?: number;
    state: string;
    lga: string;
    wards: string;
  }>({ state: "", lga: "", wards: "" });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { limit: 50 };
      if (search.trim() !== "") {
        params.search = search.trim();
      }
      const res = await api.get("/locations/", { params });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toaster.create({ title: "Error fetching locations", type: "error" });
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchData();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [fetchData]);

  const handleSave = async () => {
    if (!currentItem.state) {
      toaster.create({ title: "State is required", type: "error" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        state: currentItem.state,
        lga: currentItem.lga || null,
        wards: currentItem.wards
          ? currentItem.wards
              .split(",")
              .map((w) => w.trim())
              .filter(Boolean)
          : null,
      };

      if (isEdit && currentItem.id) {
        await api.put(`/locations/${currentItem.id}`, payload);
        toaster.create({ title: "Location updated", type: "success" });
      } else {
        await api.post("/locations", payload);
        toaster.create({ title: "Location created", type: "success" });
      }
      setIsOpen(false);
      fetchData();
    } catch (err: any) {
      const msg = err.response?.data?.detail || "Error saving location";
      toaster.create({ title: msg, type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure?")) return;
    try {
      await api.delete(`/locations/${id}`);
      toaster.create({ title: "Deleted", type: "success" });
      fetchData();
    } catch (err) {
      toaster.create({ title: "Error deleting", type: "error" });
    }
  };

  const openCreate = () => {
    setCurrentItem({ state: "", lga: "", wards: "" });
    setIsEdit(false);
    setIsOpen(true);
  };

  const openEdit = (loc: Location) => {
    setCurrentItem({
      id: loc.id,
      state: loc.state,
      lga: loc.lga || "",
      wards: loc.wards ? loc.wards.join(", ") : "",
    });
    setIsEdit(true);
    setIsOpen(true);
  };

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack align="stretch" gap={6}>
        <Stack
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align={{ base: "stretch", sm: "center" }}
          gap={4}>
          <Heading size="lg" color="blue.700">
            Manage Locations
          </Heading>
          <Button
            colorScheme="blue"
            onClick={openCreate}
            width={{ base: "full", sm: "auto" }}>
            <HStack gap={2}>
              <FiPlus />
              <Text>Add Location</Text>
            </HStack>
          </Button>
        </Stack>

        <Card.Root>
          <Card.Body>
            <Stack direction={{ base: "column", sm: "row" }} gap={4} mb={6}>
              <Input
                placeholder="Search state or LGA..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                maxW={{ base: "full", sm: "300px" }}
              />
              <IconButton
                aria-label="Refresh"
                onClick={fetchData}
                variant="ghost"
                alignSelf={{ base: "flex-end", sm: "auto" }}>
                <FiRefreshCw />
              </IconButton>
            </Stack>

            {loading ? (
              <VStack p={10}>
                <Spinner />
              </VStack>
            ) : (
              <Box overflowX="auto">
                <Table.Root striped interactive size={{ base: "sm", md: "md" }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader whiteSpace="nowrap">
                        State
                      </Table.ColumnHeader>
                      <Table.ColumnHeader whiteSpace="nowrap">
                        LGA
                      </Table.ColumnHeader>
                      <Table.ColumnHeader minW="200px">
                        Wards
                      </Table.ColumnHeader>
                      <Table.ColumnHeader>Actions</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {data.map((loc) => (
                      <Table.Row key={loc.id}>
                        <Table.Cell fontWeight="bold">{loc.state}</Table.Cell>
                        <Table.Cell>{loc.lga || "-"}</Table.Cell>
                        <Table.Cell>
                          {loc.wards ? (
                            <Text fontSize="xs" color="gray.500" lineClamp={2}>
                              {loc.wards.join(", ")}
                            </Text>
                          ) : (
                            "-"
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          <HStack>
                            <IconButton
                              size="sm"
                              aria-label="Edit"
                              variant="ghost"
                              onClick={() => openEdit(loc)}>
                              <FiEdit2 />
                            </IconButton>
                            <IconButton
                              size="sm"
                              aria-label="Delete"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => handleDelete(loc.id)}>
                              <FiTrash2 />
                            </IconButton>
                          </HStack>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                    {data.length === 0 && (
                      <Table.Row>
                        <Table.Cell colSpan={4} textAlign="center">
                          No locations found
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}
          </Card.Body>
        </Card.Root>

        <Dialog.Root open={isOpen} onOpenChange={(e) => setIsOpen(e.open)}>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Dialog.Title>
                  {isEdit ? "Edit Location" : "Add Location"}
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body>
                <VStack gap={4} align="stretch">
                  <Box>
                    <Text mb={1} fontSize="sm" fontWeight="medium">
                      State *
                    </Text>
                    <Input
                      value={currentItem.state}
                      onChange={(e) =>
                        setCurrentItem({
                          ...currentItem,
                          state: e.target.value,
                        })
                      }
                      placeholder="e.g. Lagos"
                    />
                  </Box>
                  <Box>
                    <Text mb={1} fontSize="sm" fontWeight="medium">
                      LGA
                    </Text>
                    <Input
                      value={currentItem.lga}
                      onChange={(e) =>
                        setCurrentItem({ ...currentItem, lga: e.target.value })
                      }
                      placeholder="e.g. Ikeja"
                    />
                  </Box>
                  <Box>
                    <Text mb={1} fontSize="sm" fontWeight="medium">
                      Wards (Comma separated)
                    </Text>
                    <Textarea
                      value={currentItem.wards}
                      onChange={(e) =>
                        setCurrentItem({
                          ...currentItem,
                          wards: e.target.value,
                        })
                      }
                      placeholder="Ward A, Ward B..."
                    />
                  </Box>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline">Cancel</Button>
                </Dialog.ActionTrigger>
                <Button
                  colorScheme="blue"
                  onClick={handleSave}
                  loading={saving}>
                  Save
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      </VStack>
    </Box>
  );
}
